import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  useMediaQuery,
  Stack,
  Grid,
  Paper,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUserProject } from "../../Redux/userPojectSlice/userProjectSlice";
import Navbar from "src/Components/Partials/Navbar/Navbar_2";
import { getUrl } from "src/Utils/Common";
import { DropDownDashboard } from "../Dashboard/DropDownDashboard";
import FilterComponent from "../../Components/Partials/LocationFilter/LocationFilter";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import MarketeersBackground from "./MarketeersBackground";
import backgroundProject from "src/Assets/MarketeersLogo.png";

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const options = { month: "short", day: "numeric", year: "numeric" };
  return new Intl.DateTimeFormat("en-US", options).format(date);
}

// Optional: Marketeers palette helpers
const MK_NAVY = "#08306B";

const UserProjectSelection = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isFiltersSelected, setIsFiltersSelected] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [menuConfig, setMenuConfig] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectId = useSelector((state) => state.userProject.projectId);
  const selectedFilter = useSelector((state) => state.filter.selectedFilter);
  const currentChartType = useSelector((state) => state.chart.currentChartType);
  const isAllMarketSelected = useSelector(
    (state) => state.allMarket.isAllMarketSelected
  );

  useEffect(() => {
    localStorage.removeItem("projectDates");
  }, []);

  useEffect(() => {
    function fetchHierarchyData() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }

      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          const hierarchyData = result["0"];
          const dynamicMenuConfig = {};
          Object.entries(hierarchyData).forEach(([key, values]) => {
            dynamicMenuConfig[key] = values;
          });
          setMenuConfig(dynamicMenuConfig);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy data:", error);
        });
    }

    function fetchProjects() {
      const token = localStorage.getItem("userId");
      if (!token) {
        alert("Authentication token not found. Please log in again.");
        return;
      }

      fetch(`${getUrl()}/auth/get_projects`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
      })
        .then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              alert("Unauthorized access. Please log in again.");
              navigate("/login");
            } else {
              throw new Error("Failed to fetch projects");
            }
          }
          return response.json();
        })
        .then((result) => {
          const formattedProjects = result.project_ids.map((id, index) => ({
            id,
            name: result.project_names[index],
          }));
          setProjects(formattedProjects);
        })
        .catch((error) => {
          console.error("Error fetching projects:", error);
          setError("Failed to fetch projects. Please try again.");
        });
    }

    fetchHierarchyData();
    fetchProjects();
  }, [projectId, navigate]);

  useEffect(() => {
    const isCategorySelected = currentChartType !== null;
    const isLocationSelected = selectedFilter.length > 0;

    setIsFiltersSelected(
      (isCategorySelected && isLocationSelected) ||
        (isCategorySelected && isAllMarketSelected)
    );
  }, [selectedFilter, currentChartType, isAllMarketSelected]);

  function fetchDate(project_id) {
    setLoading(true);
    fetch(
      `${getUrl()}/market_overview/start_end_dates?project_id=${project_id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((result) => {
        localStorage.setItem(
          "projectDates",
          formatDate(result.data.end_date)
        );
      })
      .catch((error) => {
        console.error("Error fetching hierarchy data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handleProjectSelection = () => {
    if (selectedProjectId) {
      navigate("/dashboard");
    } else {
      alert("Please select a project.");
    }
  };

  const handleProjectChange = (event) => {
    const selectedId = event.target.value;
    setSelectedProjectId(selectedId);
    fetchDate(selectedId);

    dispatch(setUserProject({ projectId: parseInt(selectedId, 10) }));
    localStorage.setItem("projectId", selectedId);
    localStorage.setItem("page", "Dashboard");
    localStorage.setItem("active", "dashboard");
  };

  const isActionDisabled = !selectedProjectId;

  return (
    <>
      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left visual / brand side */}
        <Grid item xs={12} md={6}>
          <MarketeersBackground src={backgroundProject} />
        </Grid>

        {/* Right side: selection card */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#F4F6FB",
            p: { xs: 2, sm: 4 },
          }}
        >
          <Paper
            elevation={4}
            sx={{
              width: "100%",
              maxWidth: 480,
              borderRadius: 4,
              p: { xs: 3, sm: 4 },
              boxShadow: "0 14px 35px rgba(0,0,0,0.08)",
            }}
          >
            <Box mb={2} textAlign="left">
              <Typography
                variant="overline"
                sx={{ color: "#929292", letterSpacing: 1 }}
              >
                Step 1
              </Typography>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: "bold",
                  color: MK_NAVY,
                }}
              >
                Select a Category
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "#555" }}
              >
                Choose a project category to load its dashboards and structures.
              </Typography>
            </Box>

            {error && (
              <Typography
                color="error"
                sx={{ mb: 2, fontSize: 14 }}
              >
                {error}
              </Typography>
            )}

            {projects.length === 0 && !error && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  gap: 1,
                }}
              >
                <FacebookCircularProgress />
                <Typography
                  variant="body2"
                  sx={{ color: "#777" }}
                >
                  Loading your projects...
                </Typography>
              </Box>
            )}

            {projects.length > 0 && (
              <TextField
                select
                fullWidth
                label="Select a category"
                value={selectedProjectId}
                onChange={handleProjectChange}
                disabled={loading}
                sx={{
                  mb: 3,
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                  },
                }}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

           

            {/* Extra space for future filters / info */}
            {selectedProjectId && projectId && (
              <Box mb={2}>
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  {/* Reserved space – you can plug in more info later */}
                </Stack>
              </Box>
            )}

            {/* Actions */}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleProjectSelection}
              disabled={isActionDisabled}
              sx={{
                fontWeight: "bold",
                textTransform: "uppercase",
                borderRadius: 3,
                py: 1.1,
                opacity: isActionDisabled ? 0.6 : 1,
                cursor: isActionDisabled ? "not-allowed" : "pointer",
              }}
            >
              Launch
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default UserProjectSelection;
